// src/utils/notify.ts
import router from '@/router'
import { toast, type ToastOptions } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

export type NotifyType = 'success' | 'error' | 'info' | 'warning'

type LinkTarget = '_blank' | '_self'

interface NotifyExtras {
  /** External URL to open when the toast is clicked */
  url?: string
  /** Open URL in new tab? (default: true = _blank) */
  newTab?: boolean
  /** Navigate to a vue-router location when clicked (ignored if url is provided) */
  route?: Parameters<typeof router.push>[0]
  /** Override autoClose (ms). Default: 6000 */
  autoClose?: number
  /** Additional ToastOptions pass-through, if you need more control */
  options?: ToastOptions
}

/**
 * Clickable toast.
 * - If `url` is provided, clicking opens the URL (new tab by default).
 * - Else if `route` is provided, clicking router.push'es to that route.
 * - Otherwise behaves like a normal toast.
 */
export function notify(message: string, type: NotifyType = 'info', extras: NotifyExtras = {}) {
  const { url, route, newTab = true, autoClose = 6000, options = {} } = extras

  const onClick = () => {
    if (url) {
      const target: LinkTarget = newTab ? '_blank' : '_self'
      window.open(url, target, 'noopener')
    } else if (route) {
      router.push(route)
    }
  }

  toast(message, {
    type,
    autoClose,
    pauseOnHover: true,
    closeOnClick: true,
    // make it obvious it's clickable
    style: url || route ? { cursor: 'pointer' } : undefined,
    // only attach handler if we actually navigate
    ...(url || route ? { onClick } : {}),
    ...options,
  })
}

/** Convenience helper for Steam trade offers */
export function notifyTradeOffer(tradeOfferId: string) {
  const url = `https://steamcommunity.com/tradeoffer/${tradeOfferId}`
  notify('Trade offer created. Click to open.', 'success', { url })
}
